import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X } from '@phosphor-icons/react';
import { toastStore, Toast, NotificationPrefs } from '../../stores/toastStore';
import './ToastStack.scss';

const CARD_HEIGHT = 72;
const CARD_GAP = 10;
const CLEAR_ALL_H = 34;
const MAX_PEEK = 3;

// Stacking offsets/transforms for collapsed peek cards (index 0 = front/top)
const PEEK_OFFSET_Y = [0, 8, 14];
const PEEK_SCALE    = [1, 0.96, 0.92];
const PEEK_OPACITY  = [1, 0.85, 0.7];

function priorityDotColor(priority?: string): string {
  switch (priority) {
    case 'high':
    case 'urgent': return '#ef4444';
    case 'low':    return 'rgba(255,255,255,0.3)';
    default:       return '#22c55e';
  }
}

// ─── ToastItem ────────────────────────────────────────────────────────────────

interface ToastItemProps {
  toast: Toast;
  index: number;
  /** Whether the whole stack is hovered (for timer pausing) */
  hovered: boolean;
  /** Whether the stack is in spread/fanned view (for positioning) */
  spread: boolean;
  removing: boolean;
  prefs: NotificationPrefs;
  onDismiss: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({
  toast,
  index,
  hovered,
  spread,
  removing,
  prefs,
  onDismiss,
}) => {
  const duration = toast.duration ?? prefs.duration * 1000;

  // Timer management — elapsed tracks how much of the duration has been consumed
  const elapsedRef   = useRef(0);
  const startTimeRef = useRef(Date.now());
  const timerRef     = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearTimer = useCallback(() => clearTimeout(timerRef.current), []);

  const startTimer = useCallback(() => {
    if (!prefs.autoDismiss) return;
    if (toast.priority === 'urgent') return; // macOS "Alert" — always requires explicit dismiss
    const remaining = Math.max(0, duration - elapsedRef.current);
    timerRef.current = setTimeout(onDismiss, remaining);
  }, [prefs.autoDismiss, toast.priority, duration, onDismiss]);

  useEffect(() => {
    startTimeRef.current = Date.now();
    startTimer();
    return clearTimer;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hovered) {
      clearTimer();
      elapsedRef.current += Date.now() - startTimeRef.current;
    } else {
      startTimeRef.current = Date.now();
      startTimer();
    }
  }, [hovered, clearTimer, startTimer]);

  // Compute position & transform based on collapsed/spread state
  const visible = removing || spread || index < MAX_PEEK;
  const peekIdx = Math.min(index, MAX_PEEK - 1);

  let topPx: number;
  let transform: string;
  let opacity: number;
  let zIndex: number;
  let pointerEvents: React.CSSProperties['pointerEvents'];

  if (spread) {
    topPx        = CLEAR_ALL_H + index * (CARD_HEIGHT + CARD_GAP);
    transform    = removing ? 'translateX(110%)' : 'translateX(0) scale(1)';
    opacity      = removing ? 0 : 1;
    zIndex       = 100 - index;
    pointerEvents = removing ? 'none' : 'auto';
  } else {
    topPx        = PEEK_OFFSET_Y[peekIdx];
    transform    = removing ? 'translateX(110%)' : `translateX(0) scale(${PEEK_SCALE[peekIdx]})`;
    opacity      = removing ? 0 : (visible ? PEEK_OPACITY[peekIdx] : 0);
    zIndex       = MAX_PEEK - index;
    pointerEvents = (index >= MAX_PEEK || removing) ? 'none' : 'auto';
  }

  const handleActionClick = () => {
    if (!toast.actionUrl) return;
    window.dispatchEvent(new CustomEvent('spacechat:openThread', {
      detail: { threadId: toast.actionUrl.split('thread=')[1] },
    }));
    onDismiss();
  };

  return (
    <div
      className="toast-item"
      style={{
        top: topPx,
        transform,
        opacity,
        zIndex,
        pointerEvents,
        cursor: toast.actionUrl ? 'pointer' : 'default',
        ['--toast-duration' as any]: `${duration}ms`,
        animationPlayState: hovered ? 'paused' : 'running',
      } as React.CSSProperties}
      onClick={toast.actionUrl ? handleActionClick : undefined}
    >
      <span
        className="toast-item__dot"
        style={{ background: priorityDotColor(toast.priority) }}
      />
      <span className="toast-item__message">{toast.message}</span>

      {toast.actionUrl && (
        <button
          className="toast-item__action"
          onClick={e => { e.stopPropagation(); handleActionClick(); }}
        >
          View →
        </button>
      )}

      <button
        className="toast-item__dismiss"
        onClick={e => { e.stopPropagation(); onDismiss(); }}
        aria-label="Dismiss"
      >
        <X size={11} weight="bold" />
      </button>

      {prefs.autoDismiss && (
        <div
          className="toast-item__progress"
          style={{
            animationDuration: `${duration}ms`,
            animationPlayState: hovered ? 'paused' : 'running',
          }}
        />
      )}
    </div>
  );
};

// ─── ToastStack ───────────────────────────────────────────────────────────────

export const ToastStack: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [prefs, setPrefs]   = useState<NotificationPrefs>(toastStore.getPrefs());
  const [hovered, setHovered] = useState(false);
  // IDs currently in their exit animation (before actual store removal)
  const [removing, setRemoving] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    return toastStore.subscribe((nextToasts, nextPrefs) => {
      setToasts(nextToasts);
      setPrefs(nextPrefs);
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setRemoving(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setTimeout(() => {
      toastStore.remove(id);
      setRemoving(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  }, []);

  const clearAll = useCallback(() => {
    // Animate all out, then clear
    const ids = toasts.map(t => t.id);
    setRemoving(new Set(ids));
    setTimeout(() => {
      toastStore.clear();
      setRemoving(new Set());
    }, 300);
  }, [toasts]);

  if (toasts.length === 0) return null;

  const isSpread = hovered && toasts.length > 1;
  const collapsedH = CARD_HEIGHT + PEEK_OFFSET_Y[Math.min(toasts.length - 1, MAX_PEEK - 1)];
  const spreadH    = CLEAR_ALL_H + toasts.length * (CARD_HEIGHT + CARD_GAP);
  const containerH = isSpread ? spreadH : collapsedH;

  return ReactDOM.createPortal(
    <div
      className={`toast-stack${isSpread ? ' is-spread' : ''}`}
      style={{ height: containerH }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Clear-all button — only visible when spread with multiple toasts */}
      <button
        className="toast-stack__clear-all"
        onClick={clearAll}
        tabIndex={isSpread ? 0 : -1}
      >
        Clear all
      </button>

      {toasts.map((toast, i) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          index={i}
          hovered={hovered}
          spread={isSpread}
          removing={removing.has(toast.id)}
          prefs={prefs}
          onDismiss={() => dismiss(toast.id)}
        />
      ))}

      {/* Count badge — only visible when collapsed with multiple toasts */}
      {!hovered && toasts.length > 1 && (
        <span className="toast-stack__count">{toasts.length}</span>
      )}
    </div>,
    document.body,
  );
};

export default ToastStack;
